import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  IconLogin2,
  IconMail,
  IconLock,
  IconEye,
  IconEyeOff,
  IconLoader2,
  IconAlertCircle,
  IconUsers,
  IconMessageCircle,
  IconTrendingUp,
} from "@tabler/icons-react";
import classes from "./loginRedesign.module.css";
import { talentLogin, talentResendOtp } from "../../../services/talentCalls";
import { clientLogin, clientResendOtp } from "../../../services/clientCalls";
import { errorHandler } from "../../../utils";
import { UserContext } from "../../../context";
import { Logo } from "../../../constants";
import { useOAuthCallbackHandler } from "../../../hooks/useOAuthCallback";

const BrandLogo = () => (
  <a className={classes.brandLogo} href="https://www.awasource.com">
    <Logo color="#ffffff" />
  </a>
);

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoginRedesign = () => {
  const navigate = useNavigate();
  const { role, setRole } = useContext(UserContext);

  const [data, setData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [load, setLoad] = useState(false);
  useOAuthCallbackHandler(setLoad);
  const [emailErr, setEmailErr] = useState(false);
  const [passwordErr, setPasswordErr] = useState(false);
  const [emailShake, setEmailShake] = useState(false);
  const [passwordShake, setPasswordShake] = useState(false);

  /* Lock page scroll on desktop, where this is a fixed one-screen split layout */
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 981px)");
    const applyLock = () => {
      const locked = mql.matches ? "hidden" : "";
      document.documentElement.style.overflow = locked;
      document.body.style.overflow = locked;
    };
    applyLock();
    mql.addEventListener("change", applyLock);
    return () => {
      mql.removeEventListener("change", applyLock);
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, []);

  const shakeField = (setShake) => {
    setShake(true);
    setTimeout(() => setShake(false), 400);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let ok = true;
    if (!emailRe.test(data.email.trim())) {
      setEmailErr(true);
      shakeField(setEmailShake);
      ok = false;
    }
    if (!data.password) {
      setPasswordErr(true);
      shakeField(setPasswordShake);
      ok = false;
    }
    if (!ok) return;

    const params = new URLSearchParams(window.location.search);
    const redirectParam = params.get("redirect");
    const decodedRedirect = redirectParam ? decodeURIComponent(redirectParam) : null;

    const isSafeRedirect =
      decodedRedirect &&
      decodedRedirect.startsWith("/") &&
      !decodedRedirect.startsWith("//") &&
      decodedRedirect !== "/login";

    const safeRedirect = isSafeRedirect ? decodedRedirect : null;

    try {
      setLoad(true);

      if (role === "talent") {
        const res = await talentLogin(data);
        localStorage.setItem("token", res?.data?.data?.token);
        localStorage.setItem("role", "talent");
        localStorage.setItem("email", data.email);
        navigate(safeRedirect ?? "/ai-insights", { replace: true });
      } else {
        const res = await clientLogin(data);
        localStorage.setItem("token", res?.data?.data?.token);
        localStorage.setItem("role", "client");
        localStorage.setItem("email", res?.data?.data?.email || data.email);
        navigate(safeRedirect ?? "/client-activity-summary", { replace: true });
      }
    } catch (error) {
      setLoad(false);
      const response = error?.response;
      const dataRes = response?.data;

      let errorMessage = "Network error: Please check your internet connection and try again.";
      if (dataRes?.message) {
        errorMessage = dataRes.message;
      } else if (dataRes?.errors) {
        errorMessage = dataRes.errors;
      }

      errorHandler(errorMessage, "Login error");

      const UNVERIFIED_MSG = "Unverified account: Please check your email to verify your account..";

      if (dataRes?.errors === UNVERIFIED_MSG) {
        localStorage.setItem("email", data.email);
        setTimeout(() => {
          if (role === "talent") {
            talentResendOtp();
            navigate("/verify-mail?status=talent");
          } else {
            clientResendOtp();
            navigate("/verify-mail?status=client");
          }
        }, 2000);
      }
    }
  };

  return (
    <div className={classes.authShell}>
      <div className={classes.pageBg}>
        <div className={`${classes.brandBlob} ${classes.b1}`} />
        <div className={`${classes.brandBlob} ${classes.b2}`} />
        <div className={`${classes.brandBlob} ${classes.b3}`} />
      </div>

      <aside className={classes.brandPanel}>
        <div className={classes.brandTop}>
          <BrandLogo />
          <span className={classes.brandPanelTag}>Africa's hiring layer</span>
        </div>

        <div className={classes.brandMiddle}>
          <h1 className={classes.brandHeadline}>
            Good to have
            <br />
            you back.
          </h1>
          <p className={classes.brandSub}>
            Pick up right where you left off — new matches, messages, and insights are already waiting for you.
          </p>

          <div className={classes.brandVisual}>
            <div className={`${classes.floatCard} ${classes.fcA}`}>
              <span className={classes.fcIcon} style={{ background: "#eff2ff", color: "#1d4ed8" }}>
                <IconUsers size={16} />
              </span>
              <div>
                <div className={classes.fcTitle}>12 new matches</div>
                <div className={classes.fcSub}>Since your last visit</div>
              </div>
            </div>
            <div className={`${classes.floatCard} ${classes.fcB}`}>
              <span className={classes.fcIcon} style={{ background: "#fef3c7", color: "#d97706" }}>
                <IconMessageCircle size={16} />
              </span>
              <div>
                <div className={classes.fcTitle}>3 unread messages</div>
                <div className={classes.fcSub}>From active conversations</div>
              </div>
            </div>
            <div className={`${classes.floatCard} ${classes.fcC}`}>
              <span className={classes.fcIcon} style={{ background: "#ecfdf5", color: "#059669" }}>
                <IconTrendingUp size={16} />
              </span>
              <div>
                <div className={classes.fcTitle}>Profile 92% strong</div>
                <div className={classes.fcSub}>Almost fully optimized</div>
              </div>
            </div>
          </div>
        </div>

        <div className={classes.brandBottom}>
          <div className={classes.brandBottomAvatars}>
            <span>AO</span>
            <span>KM</span>
            <span>NB</span>
          </div>
          <p className={classes.brandBottomText}>
            <strong>2,000+</strong> people signed in today
          </p>
        </div>
      </aside>

      <div className={classes.mobileHeaderBar}>
        <div className={classes.mobileHeaderBarInner}>
          <BrandLogo />
        </div>
      </div>

      <main className={classes.formPanel}>
        <div className={classes.formCard}>
          <div className={classes.formPanelInner}>
            <div className={`${classes.formHeader} ${classes.animIn}`}>
              <span className={classes.formEyebrow}>
                <IconLogin2 size={13} /> Welcome back
              </span>
              <h1 className={classes.formTitle}>Sign in to Awasource.</h1>
              <p className={classes.formSubtitle}>Enter your details to access your account.</p>
            </div>

            <p className={`${classes.formTopLink} ${classes.animIn}`}>
              Don't have an Account? <Link to="/register">Sign Up</Link>
            </p>

            <div className={`${classes.roleToggle} ${classes.animIn}`}>
              <div className={`${classes.roleToggleHighlight} ${role !== "talent" ? classes.roleToggleHighlightClient : ""}`} />
              <button
                type="button"
                className={`${classes.roleToggleBtn} ${role === "talent" ? classes.roleToggleBtnActive : ""}`}
                onClick={() => setRole("talent")}
              >
                Talent
              </button>
              <button
                type="button"
                className={`${classes.roleToggleBtn} ${role !== "talent" ? classes.roleToggleBtnActive : ""}`}
                onClick={() => setRole("client")}
              >
                Client
              </button>
            </div>

            <form className={classes.animIn} onSubmit={handleSubmit} noValidate>
              <div className={classes.field}>
                <label className={classes.fieldLabel} htmlFor="signinEmail">
                  Email Address
                </label>
                <div className={classes.inputWrap}>
                  <IconMail size={16} className={classes.inputIcon} />
                  <input
                    id="signinEmail"
                    className={`${classes.formInput} ${emailErr ? classes.formInputErr : ""} ${emailShake ? classes.shake : ""}`}
                    type="email"
                    placeholder="you@company.com"
                    autoComplete="email"
                    value={data.email}
                    onChange={(e) => {
                      setData((p) => ({ ...p, email: e.target.value }));
                      setEmailErr(false);
                    }}
                  />
                </div>
                <div className={`${classes.fieldError} ${emailErr ? classes.fieldErrorOn : ""}`}>
                  <IconAlertCircle size={13} /> Enter a valid email address.
                </div>
              </div>

              <div className={classes.field}>
                <label className={classes.fieldLabel} htmlFor="signinPassword">
                  Password
                </label>
                <div className={classes.inputWrap}>
                  <IconLock size={16} className={classes.inputIcon} />
                  <input
                    id="signinPassword"
                    className={`${classes.formInput} ${passwordErr ? classes.formInputErr : ""} ${passwordShake ? classes.shake : ""}`}
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    style={{ paddingRight: 44 }}
                    value={data.password}
                    onChange={(e) => {
                      setData((p) => ({ ...p, password: e.target.value }));
                      setPasswordErr(false);
                    }}
                  />
                  <button
                    type="button"
                    className={classes.pwToggleBtn}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((p) => !p)}
                  >
                    {showPassword ? <IconEyeOff size={17} /> : <IconEye size={17} />}
                  </button>
                </div>
                <div className={`${classes.fieldError} ${passwordErr ? classes.fieldErrorOn : ""}`}>
                  <IconAlertCircle size={13} /> Please enter your password.
                </div>
              </div>

              <button type="submit" className={classes.btnPrimary} disabled={load}>
                {load ? (
                  <>
                    <IconLoader2 size={16} className={classes.spinIcon} /> Signing in…
                  </>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>

            <p className={classes.forgotLinkRow}>
              <Link className={classes.forgotLink} to={`/reset-password?status=${role}`}>
                Forgot Password
              </Link>
            </p>

            <p className={classes.formFooter}>
              By proceeding you have agreed to our <Link to="/terms-of-service">Terms of Use</Link> &amp;{" "}
              <Link to="/privacy-policy">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginRedesign;
